import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { registerLicense } from '@syncfusion/ej2-base';

import App from './App';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { setQueryCacheClearer } from '@/services/api';

// Initialize i18n before rendering
import './i18n';

import './index.css';

// Register Syncfusion license key (required for PDF Viewer)
// Get your free community license at: https://www.syncfusion.com/account/manage-trials/downloads
registerLicense(import.meta.env.VITE_SYNCFUSION_LICENSE_KEY || '');

/**
 * React Query client configuration
 * - 5 minute stale time for caching
 * - 3 retry attempts for failed queries
 * - Refetch on window focus for fresh data
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 3,
      refetchOnWindowFocus: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Wire the API client's logout / auth-failure paths to clear the React Query
// cache on this singleton. Prevents cross-user data bleed on shared devices
// (fe-auth-client-01). Injected via a setter to avoid an import cycle.
setQueryCacheClearer(() => queryClient.clear());

const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error(
    'Root element not found. Make sure there is a <div id="root"></div> in your index.html.'
  );
}

createRoot(rootElement).render(
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
);
